import { http, HttpResponse } from 'msw';
import { providerRefs, siteRefs } from '../../factories/seeds/entities';
import { mockProviders } from './providerMock';
import { mockSites } from './siteMock';

const providerByID = new Map(
  mockProviders.map((provider) => [provider.id, provider])
);
const siteByID = new Map(mockSites.map((site) => [site.id, site]));

const canadaLifeSite = siteByID.get(siteRefs.canadaLifeCentre.id) || null;
const qdocSite = siteByID.get(siteRefs.qdocVirtualHealthcare.id) || null;
const canadaLifeProvider =
  providerByID.get(providerRefs.normanSilver.id) || null;
const qdocProvider = providerByID.get(providerRefs.adamDonen.id) || null;

// Minimal IntakeItem mocks aligned with @billing/validation/intakeItem
export const mockIntakeItems = [
  {
    id: '00000000-0000-0000-0000-000000000001',
    siteID: canadaLifeSite?.id ?? '',
    providerID: canadaLifeProvider?.id ?? '',
    createdAt: '2024-01-01T00:00:00.000Z',
    completedAt: null,
    status: 'Open',
    notes: 'Initial intake item',
    assigneeUserID: null,
    intakeNumber: 123,
  },
  {
    id: '00000000-0000-0000-0000-000000000002',
    siteID: qdocSite?.id ?? '',
    providerID: qdocProvider?.id ?? '',
    createdAt: '2024-02-15T12:00:00.000Z',
    completedAt: null,
    status: 'In Progress',
    notes: 'Follow-up required',
    assigneeUserID: null,
    intakeNumber: 456,
  },
];

const paginate = (items: any[], page: number, pageSize: number) => {
  const start = page * pageSize;
  const end = start + pageSize;
  const sliced = items.slice(start, end);
  const total = items.length;
  const pages = Math.ceil(total / pageSize) || 1;
  const nextPage = Math.min(page + 1, pages);
  return { data: sliced, total, pages, page: page, limit: pageSize, nextPage };
};

export const mockIntakeItemHandlers = [
  http.post('*/resource/intakeItem', async ({ request }) => {
    const body = (await request.json()) as any;
    return HttpResponse.json({
      id:
        body.id ??
        `00000000-0000-0000-0000-00000000000${mockIntakeItems.length + 1}`,
      intakeNumber: mockIntakeItems.length + 100,
      completedAt: null,
      notes: null,
      assigneeUserID: null,
      ...body,
    });
  }),

  // Status counts by site
  http.get('*/resource/intakeItem/status/count*', ({ request }) => {
    const url = new URL(request.url);
    const siteID = url.searchParams.get('siteID');
    const providerID = url.searchParams.get('providerID');
    const filtered = mockIntakeItems.filter((item) => {
      if (siteID && item.siteID !== siteID) return false;
      if (providerID && item.providerID !== providerID) return false;
      return true;
    });

    const counts = filtered.reduce<Record<string, number>>((acc, item) => {
      const status = item.status || '';
      if (!status) return acc;
      acc[status] = (acc[status] ?? 0) + 1;
      return acc;
    }, {});

    return HttpResponse.json(counts);
  }),

  // Paginate by site
  http.get('*/resource/intakeItem/site/page*', ({ request }) => {
    const url = new URL(request.url);
    const siteID = url.searchParams.get('siteID');
    const page = Number(url.searchParams.get('page') ?? '0');
    const pageSize = Number(url.searchParams.get('pageSize') ?? '10');
    const filtered = siteID
      ? mockIntakeItems.filter((i) => i.siteID === siteID)
      : mockIntakeItems;
    return HttpResponse.json(paginate(filtered, page, pageSize));
  }),

  // Paginate by provider
  http.get('*/resource/intakeItem/provider/page*', ({ request }) => {
    const url = new URL(request.url);
    const providerID = url.searchParams.get('providerID');
    const siteID = url.searchParams.get('siteID');
    const page = Number(url.searchParams.get('page') ?? '0');
    const pageSize = Number(url.searchParams.get('pageSize') ?? '10');
    const filtered = mockIntakeItems.filter((item) => {
      if (providerID && item.providerID !== providerID) return false;
      if (siteID && item.siteID !== siteID) return false;
      return true;
    });
    return HttpResponse.json(paginate(filtered, page, pageSize));
  }),

  // Update intake item
  http.put('*/resource/intakeItem', async ({ request }) => {
    const body = await request.json();
    return HttpResponse.json(body);
  }),
];
